// Iter · Feb 2026 — Workspace-editable content categories.
// Simple CRUD dialog: list categories, add / rename / recolor / disable.
// Backend endpoints:
//   GET/POST  /api/v1/workspaces/{ws}/content-categories
//   PATCH/DELETE /api/v1/content-categories/{cid}

import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Plus, Trash2, Loader2, ArrowRightLeft } from "lucide-react";

const SWATCHES = ["#8b5cf6", "#ec4899", "#f97316", "#eab308", "#22c55e", "#0ea5e9", "#6366f1", "#ef4444", "#14b8a6"];

export default function ManageCategoriesDialog({ wsId, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(SWATCHES[0]);

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/content-categories?with_counts=true`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Load failed"));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, wsId]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post(`/workspaces/${wsId}/content-categories`, { name, color: newColor });
      setNewName("");
      toast.success(`Added "${name}"`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };

  const patch = async (row, upd) => {
    if (row.legacy) {
      // Adopt a legacy hard-coded category by creating a real row first,
      // then applying the patch on the new record.
      try {
        const r = await api.post(`/workspaces/${wsId}/content-categories`, {
          name: row.name, color: upd.color || null, icon_key: upd.icon_key || null,
        });
        await load();
        return r.data;
      } catch (e) { toast.error(apiErrorMessage(e, "Adopt failed")); return; }
    }
    try {
      const r = await api.patch(`/content-categories/${row.id}`, upd);
      // Feb 2026 · Backend now propagates name renames to every
      // dependent record. Surface the counts so the user knows the
      // Activity Master / Content Calendar / package lines were
      // updated in the same call.
      const applied = r?.data?.rename_applied;
      if (applied) {
        const a = applied.activity_types || 0;
        const i = applied.content_items || 0;
        const p = applied.package_lines || 0;
        if (a + i + p > 0) {
          toast.success(
            `Renamed · ${a} activit${a === 1 ? "y" : "ies"} · ${i} item${i === 1 ? "" : "s"} · ${p} package line${p === 1 ? "" : "s"} re-tagged`
          );
        }
      }
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  // Iter · Feb 2026 — Rename a legacy category by migrating every
  // reference to a new name. Creates the target custom category (if
  // missing) then hits the /migrate endpoint. Runs from the inline
  // edit-on-blur handler in the row.
  const renameLegacy = async (row, nextName) => {
    const to = (nextName || "").trim();
    if (!to || to === row.name) return;
    try {
      // Ensure a real (non-legacy) row with the target name exists.
      const list = await api.get(`/workspaces/${wsId}/content-categories`);
      const existing = (list.data || []).find(
        (r) => !r.legacy && r.name.toLowerCase() === to.toLowerCase()
      );
      if (!existing) {
        await api.post(`/workspaces/${wsId}/content-categories`, { name: to, color: row.color || null });
      }
      const r = await api.post(`/workspaces/${wsId}/content-categories/migrate`, {
        from_category: row.name, to_category: to, dry_run: false,
      });
      const a = r.data.applied || {};
      toast.success(`Renamed ${row.name} → ${to} · ${a.activity_types || 0} activities · ${a.content_items || 0} items · ${a.package_lines || 0} lines`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Rename failed")); }
  };

  const remove = async (row) => {
    if (row.legacy) { toast.info("Legacy category — nothing to delete"); return; }
    if (!confirm(`Delete category "${row.name}"?`)) return;
    try {
      await api.delete(`/content-categories/${row.id}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // ── Iter · Feb 2026 — Migration wizard state ────────────────────
  const [migFrom, setMigFrom] = useState("");
  const [migTo, setMigTo] = useState("");
  const [migPreview, setMigPreview] = useState(null);
  const [migLoading, setMigLoading] = useState(false);

  const migPreviewRun = async () => {
    if (!migFrom || !migTo) return;
    setMigLoading(true);
    setMigPreview(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/content-categories/migrate`, {
        from_category: migFrom, to_category: migTo, dry_run: true,
      });
      setMigPreview(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setMigLoading(false); }
  };

  const migApply = async () => {
    if (!migPreview) return;
    setMigLoading(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/content-categories/migrate`, {
        from_category: migFrom, to_category: migTo, dry_run: false,
      });
      const a = r.data.applied || {};
      toast.success(`Migrated ${a.activity_types || 0} activities · ${a.content_items || 0} items · ${a.package_lines || 0} lines`);
      setMigFrom(""); setMigTo(""); setMigPreview(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setMigLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="manage-cats-dialog">
        <DialogHeader>
          <DialogTitle>Manage content categories</DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-muted-foreground">
          Categories tag every content-calendar deliverable. Add your own (e.g. <b>Reel</b>, <b>Podcast</b>, <b>Newsletter</b>) — activity types can then default to any category.
        </div>

        {/* Add row */}
        <div className="border border-border p-3 bg-muted/20 space-y-2">
          <div className="flex items-center gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder="New category name (e.g. Reel)"
                    className="rounded-none h-8" data-testid="manage-cats-new-name" />
            <div className="flex items-center gap-1">
              {SWATCHES.map((sw) => (
                <button key={sw} className={`w-5 h-5 rounded-full border-2 ${sw === newColor ? "border-foreground" : "border-transparent"}`}
                        style={{ background: sw }}
                        onClick={() => setNewColor(sw)}
                        title={sw}
                        data-testid={`manage-cats-swatch-${sw}`} />
              ))}
            </div>
            <Button size="sm" className="rounded-none h-8" onClick={add} disabled={!newName.trim()}
                    data-testid="manage-cats-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
        ) : (
          <div className="border border-border max-h-72 overflow-auto" data-testid="manage-cats-list">
            {rows.map((row) => (
              <div key={row.id} className={`grid grid-cols-[24px,1fr,auto,auto] items-center gap-2 px-3 py-2 border-b border-border/60 ${row.active === false ? "opacity-60" : ""}`}
                    data-testid={`manage-cats-row-${row.id}`}>
                <span className="w-4 h-4 rounded-full" style={{ background: row.color || "#8b5cf6" }} />
                <div className="flex items-center gap-2 min-w-0">
                  <input
                    className="text-[12px] font-medium bg-transparent border-b border-transparent hover:border-border focus:border-foreground focus:outline-none min-w-0 flex-1 truncate"
                    defaultValue={row.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v === row.name) return;
                      if (row.legacy) renameLegacy(row, v);
                      else patch(row, { name: v });
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    data-testid={`manage-cats-name-${row.id}`}
                  />
                  {row.counts && row.counts.total > 0 && (
                    <span
                      className={`shrink-0 text-[9px] px-1.5 py-0.5 border ${row.legacy && row.counts.total > 0 ? "border-amber-500/40 bg-amber-500/10 text-amber-700" : "border-border bg-muted/40 text-muted-foreground"}`}
                      title={`Activity types: ${row.counts.activity_types} · Package lines: ${row.counts.package_lines} · Content items: ${row.counts.content_items}`}
                      data-testid={`manage-cats-count-${row.id}`}
                    >
                      in use by {row.counts.total}
                    </span>
                  )}
                  {row.legacy && <span className="shrink-0 text-[9px] text-muted-foreground uppercase tracking-widest">Legacy</span>}
                </div>
                <div className="flex items-center gap-1">
                  {SWATCHES.map((sw) => (
                    <button key={sw} className={`w-4 h-4 rounded-full border-2 ${sw === row.color ? "border-foreground" : "border-transparent"}`}
                            style={{ background: sw }}
                            onClick={() => patch(row, { color: sw })} />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  {!row.legacy && (
                    <button
                      className="text-[10px] px-2 py-0.5 border border-border hover:bg-muted"
                      onClick={() => patch(row, { active: row.active === false })}
                      data-testid={`manage-cats-toggle-${row.id}`}>
                      {row.active === false ? "Enable" : "Disable"}
                    </button>
                  )}
                  <button className="text-muted-foreground hover:text-rose-600"
                          onClick={() => remove(row)}
                          disabled={row.legacy}
                          data-testid={`manage-cats-delete-${row.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {rows.length === 0 && <div className="p-6 text-[11px] text-muted-foreground text-center">No categories yet — add your first above.</div>}
          </div>
        )}

        {/* Iter · Feb 2026 — Migration wizard. Pick a source (usually a
             legacy category) and a target (usually a custom row) to
             bulk re-tag every activity type + package line + content
             item in one shot. */}
        <div className="border border-border p-3 bg-muted/10 space-y-2" data-testid="manage-cats-migrate">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] uppercase tracking-widest font-semibold">Migrate category</span>
            <span className="text-[10px] text-muted-foreground">Bulk re-tag activities · package lines · content items</span>
          </div>
          <div className="grid grid-cols-[1fr,auto,1fr,auto] gap-2 items-center">
            <select value={migFrom} onChange={(e) => { setMigFrom(e.target.value); setMigPreview(null); }}
                    className="h-8 rounded-none border border-border text-[12px] px-2 bg-white"
                    data-testid="manage-cats-mig-from">
              <option value="">From…</option>
              {rows.map((r) => <option key={r.id} value={r.name}>{r.name}{r.legacy ? " (legacy)" : ""}</option>)}
            </select>
            <span className="text-muted-foreground">→</span>
            <select value={migTo} onChange={(e) => { setMigTo(e.target.value); setMigPreview(null); }}
                    className="h-8 rounded-none border border-border text-[12px] px-2 bg-white"
                    data-testid="manage-cats-mig-to">
              <option value="">To…</option>
              {rows.filter((r) => !r.legacy).map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
            <Button size="sm" className="rounded-none h-8" onClick={migPreviewRun}
                    disabled={!migFrom || !migTo || migFrom === migTo || migLoading}
                    data-testid="manage-cats-mig-preview">
              {migLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Preview"}
            </Button>
          </div>
          {migPreview && (
            <div className="flex items-center gap-3 text-[11px] p-2 border border-primary/30 bg-primary/5"
                 data-testid="manage-cats-mig-preview-result">
              Will re-tag: <b>{migPreview.counts.activity_types}</b> activities ·
              <b>{migPreview.counts.package_lines}</b> package lines ·
              <b>{migPreview.counts.content_items}</b> content items
              <Button size="sm" className="rounded-none h-7 ml-auto" onClick={migApply} disabled={migLoading}
                      data-testid="manage-cats-mig-apply">
                {migLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : `Apply migration`}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
